import type { FC, PropsWithChildren } from 'react'

import { config } from '@skytech/config'
import { ExternalLink, Icon, LoaderCircle } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { useQuery } from '@tanstack/react-query'
import CartIcon from 'mdi-react/CartIcon'
import ChevronRightIcon from 'mdi-react/ChevronRightIcon'
import FileDocumentOutlineIcon from 'mdi-react/FileDocumentOutlineIcon'
import MessageTextOutlineIcon from 'mdi-react/MessageTextOutlineIcon'
import PhoneOffIcon from 'mdi-react/PhoneOffIcon'
import PhonePlusIcon from 'mdi-react/PhonePlusIcon'

import { Translation } from '@/app/translations/Translation'
import RouterLink from '@/components/RouterLink'
import { USER_ROLE, useIsAdmin, useIsSkytechWL, useIsTelia, useIsThreeStepIT } from '@/hooks/access'
import useUser, { useProfile } from '@/hooks/user'
import { leasingResources } from '@/resources/leasing'
import { commonQueries } from '@/resources/queries'
import { APP_ROUTES } from '@/util/app-routes'

import { NotificationCard } from './NotificationCard'
import queries from './queries'
import dashboardResources from './resources'

import './Dashboard.styles.scss'

const isDevelopmentMode = config.ENVIRONMENT === 'dev'

export const Dashboard: FC = () => {
  const profile = useProfile()
  const user = useUser()
  const isSkytechWL = useIsSkytechWL()
  const isThreeStepIT = useIsThreeStepIT()
  const isTelia = useIsTelia()
  const isAdmin = useIsAdmin()

  const { data, isLoading } = useQuery(queries.dashboardStatus(), dashboardResources.get)
  const { data: returnAssets, isLoading: isLoadingReturnAssets } = useQuery(
    commonQueries.assetsToReturnCount(),
    leasingResources.getAssetsToReturnCount
  )

  if (isLoading || isLoadingReturnAssets) {
    return (
      <Container className="dashboard">
        <LoaderCircle />
      </Container>
    )
  }

  if (!data) return null

  const setUserGuideFileName = (): string => {
    if (isThreeStepIT) return 'User_guide_Manager_portal_3stepIT.pdf'
    if (isTelia) return '/telia_manager_brukerveiledning.pdf'
    return '/skytech_manager_brukerveiledning.pdf'
  }
  const isUserGuideVisible = isSkytechWL || isThreeStepIT || isTelia
  const userGuideFileName = setUserGuideFileName()

  const { devicePolicy, salaryDeduction, injuryReport, ecom, tradeInOrders } = data
  const deviceOrderCount = devicePolicy.pendingApplicationCount
  const webshopOrderCount = ecom.pendingApplicationCount ?? 0
  const privateAppCount = salaryDeduction.pendingApplicationCount ?? 0
  const damageReportCount = injuryReport.pendingApplicationCount
  const tradeInOrdersCount = tradeInOrders.pendingActionsCount ?? 0

  const assetsForReturnCount = returnAssets ?? 0
  const totalNotificationCount =
    deviceOrderCount +
    webshopOrderCount +
    privateAppCount +
    damageReportCount +
    assetsForReturnCount +
    tradeInOrdersCount

  return (
    <Container className="flex flex-col">
      {totalNotificationCount > 0 && (
        <div className="dashboard-text">
          <Container.Header>
            <Translation
              id="Welcome, {userName}!"
              userName={
                profile?.name ? profile.name.charAt(0).toUpperCase() + profile.name.slice(1) : ''
              }
            />
          </Container.Header>
          <p className="dashboard-message">
            <Translation id="You have some new tasks to approve:" />
          </p>
        </div>
      )}
      <div className="mb-4 flex w-full flex-wrap justify-center gap-4 md:mb-0 md:justify-start md:gap-8">
        <NotificationCard
          to={APP_ROUTES.DEVICE_ORDERS}
          count={deviceOrderCount}
          cardTitle={<Translation id="Device Orders" />}
        />

        <NotificationCard
          // Status filter: Awaiting approval from manager
          to={`${APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER_LIST}?status=5`}
          count={webshopOrderCount}
          cardTitle={<Translation id="Webshop Orders" />}
        />

        <NotificationCard
          to={APP_ROUTES.EMPLOYEES_SALARY_DEDUCTION_APPLICATIONS}
          count={privateAppCount}
          cardTitle={<Translation id="Private Applications" />}
        />

        <NotificationCard
          to={APP_ROUTES.HARDWARE_INJURY_REPORTS}
          count={damageReportCount}
          cardTitle={<Translation id="Damage Reports" />}
        />

        {/* TODO: enable for production */}
        {isDevelopmentMode && (
          <NotificationCard
            to={`${APP_ROUTES.HARDWARE_ASSETS}?assetOwnership=LEASED&assetStatus=USE&leaseReturn=90&entireConcern=true`}
            count={assetsForReturnCount}
            cardTitle={<Translation id="Assets for return" />}
          />
        )}

        <NotificationCard
          to={APP_ROUTES.HARDWARE_TRADE_IN}
          count={tradeInOrdersCount}
          cardTitle={<Translation id="Trade-in orders" />}
        />

        {totalNotificationCount === 0 && (
          <div className="mobile-adjusted-text">
            <Container.Header>
              <Translation
                id="Welcome, {userName}!"
                userName={
                  profile?.name ? profile.name.charAt(0).toUpperCase() + profile.name.slice(1) : ''
                }
              />
            </Container.Header>
            <p className="dashboard-message">
              <Translation id="Good job, you have no outstanding tasks." />
            </p>
          </div>
        )}
      </div>
      {user?.role !== USER_ROLE.REPORT_USER && (
        <div className="ml-4 mt-4 grid grid-cols-2 items-stretch gap-3 md:ml-0 md:mr-0 md:flex md:flex-row md:justify-start">
          <ChevronLink
            to={APP_ROUTES.HARDWARE_SHOP}
            className="dashboard-notification-manual-link shadow-basic flex min-h-[60px] w-[160px] items-center gap-6 rounded-md md:w-fit md:gap-3"
          >
            <Icon>
              <CartIcon className="ml-2 h-5 w-5" />
            </Icon>
            <Translation id="New Order" />
          </ChevronLink>

          <ChevronLink
            to={APP_ROUTES.SUBSCRIPTIONS_NEW_SUBSCRIPTION_LIST}
            className="dashboard-notification-manual-link shadow-basic flex min-h-[60px] w-[160px] items-center gap-3 rounded-md md:max-w-[170px]"
          >
            <Icon>
              <PhonePlusIcon className="ml-1.5 h-5 w-5" />
            </Icon>
            <Translation id="New Subscription" />
          </ChevronLink>

          <ChevronLink
            to={APP_ROUTES.SUBSCRIPTIONS_TERMINATION_LIST}
            className="dashboard-notification-manual-link shadow-basic flex min-h-[60px] w-[160px] items-center gap-2 rounded-md md:max-w-[170px]"
          >
            <Icon>
              <PhoneOffIcon className="ml-2 mr-0.5 h-5 w-5" />
            </Icon>
            <Translation id="Terminations" />
          </ChevronLink>
          {isAdmin && (
            <ChevronLink
              to={APP_ROUTES.ADMIN_SMS_SENDER}
              className="dashboard-notification-manual-link shadow-basic flex min-h-[60px] w-[160px] items-center gap-3 rounded-md md:max-w-[170px]"
            >
              <Icon>
                <MessageTextOutlineIcon className="ml-2 mr-0 h-5 w-5" />
              </Icon>
              <div className="mr-12">
                <Translation id="SMS" />
              </div>
            </ChevronLink>
          )}
          {isUserGuideVisible && (
            <ExternalLink
              className="dashboard-notification-manual-link shadow-basic flex min-h-[60px] w-[160px] items-center gap-1 rounded-md text-sm md:w-fit md:max-w-[160px]"
              hasUnderline={false}
              linkColorMode="gray"
              href={userGuideFileName}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon>
                <FileDocumentOutlineIcon className="mx-2 h-5 w-5" />
              </Icon>
              <Translation id="See user manual" className="tracking-tight" />
              <Icon className="mr-1">
                <ChevronRightIcon />
              </Icon>
            </ExternalLink>
          )}
        </div>
      )}
    </Container>
  )
}

const ChevronLink: FC<PropsWithChildren<{ to: string; className?: string }>> = ({
  to,
  children,
  className,
}) => (
  <RouterLink to={to} className={className} hasUnderline={false} colorMode="gray">
    {children}
    <Icon className="mr-1">
      <ChevronRightIcon />
    </Icon>
  </RouterLink>
)
